import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MfgappsEventService } from 'src/app/services/mfgapps-event.service';

// ── NAMC-Shop Detail interfaces ───────────────────────────────────────────
interface ShopZone {
  name: string;
  deploymentPct: number;
  color: string;
}

interface ShopEquipmentRow {
  category: string;
  planned: number;
  configured: number;
  connected: number;
  statusPct: number;
  statusLevel: 'good' | 'warn' | 'critical';
}

interface ShopOpenAction {
  action: string;
  priority: 'High' | 'Medium' | 'Low';
  dueDate: string;
}

/** Month indices: 0 = Jul 2025 … 11 = Jun 2026 */
interface ShopTimelineRow {
  area: string;
  color: string;
  plannedStart: number;
  plannedEnd: number;
  inProgressStart?: number;
  inProgressEnd?: number;
  deployedStart?: number;
  deployedEnd?: number;
  progressPct: number;
  progressDelta?: string;
}

interface ShopSiteMetadata {
  shopAdmin: string;
  targetStart: string;
  targetPilotLineComplete: string;
  deploymentPhase: string;
  lastUpdatedBy: string;
  lastUpdated: string;
}

interface NamcShopDetailData {
  notStarted?: boolean;
  zones: ShopZone[];
  equipment: ShopEquipmentRow[];
  openActions: ShopOpenAction[];
  timeline: ShopTimelineRow[];
  metadata: ShopSiteMetadata;
}

type PinLabelDirection = 'up' | 'down' | 'left' | 'right';
type MapPinStatus = 'enabled' | 'progress' | 'planned';

interface PinPosition {
  left: string;
  top: string;
  labelDirection: PinLabelDirection;
  /** Perpendicular shift (px) applied to the label to de-clutter crowded pins. */
  labelNudge?: string;
  /** Leader line length (px). Stagger this to separate closely spaced labels. */
  leaderLen?: string;
}

interface DeploymentMapPin extends PinPosition {
  code: string;
  value: string;
  companyId: number;
  codePlant: string;
  status: MapPinStatus;
}

interface PinPdmMetrics {
  planned: number;
  connected: number;
  statusPct: number;
}

interface PinDashboardData {
  phase: string;
  nextMilestone: string;
  shopConnectivityPct: number | null;
  equipmentConnected: number;
  equipmentPlanned: number;
  pdm: PinPdmMetrics;
}

@Component({
  selector: 'app-deployment-namc-shop',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule
  ],
  templateUrl: './deployment-namc-shop.component.html',
  styleUrl: './deployment-namc-shop.component.scss'
})
export class DeploymentNamcShopComponent {

  readonly namcShopMockData: Record<string, NamcShopDetailData> = {
    'TMMMO|Body Weld Plant|Body Weld Shop': {
      zones: [
        { name: 'Paint Booths',     deploymentPct: 85,  color: '#31a36f' },
        { name: 'Sub Assembly',     deploymentPct: 72,  color: '#f58220' },
        { name: 'Main Line',        deploymentPct: 60,  color: '#3d7cbf' },
        { name: 'Final Assembly',   deploymentPct: 90,  color: '#7d4db5' },
        { name: 'Test & Rework',    deploymentPct: 100, color: '#3eb8c9' },
        { name: 'Material Staging', deploymentPct: 40,  color: '#d94b4b' }
      ],
      equipment: [
        { category: 'PLCs',           planned: 67,  configured: 48,  connected: 46,  statusPct: 72,  statusLevel: 'warn' },
        { category: 'DXM Modules',    planned: 54,  configured: 39,  connected: 36,  statusPct: 72,  statusLevel: 'warn' },
        { category: 'Sensors',        planned: 215, configured: 138, connected: 114, statusPct: 64,  statusLevel: 'warn' },
        { category: 'Industrial PCs', planned: 18,  configured: 14,  connected: 14,  statusPct: 78,  statusLevel: 'warn' },
        { category: 'Gateways',       planned: 6,   configured: 5,   connected: 5,   statusPct: 83,  statusLevel: 'good' },
        { category: 'Servers',        planned: 4,   configured: 4,   connected: 4,   statusPct: 100, statusLevel: 'good' }
      ],
      openActions: [
        { action: 'Configure DXM for Press 45',       priority: 'High',   dueDate: 'May 28, 2026' },
        { action: 'Validate PLC mapping – Line 3',    priority: 'High',   dueDate: 'May 28, 2026' },
        { action: 'Install sensors – Station 12',     priority: 'Medium', dueDate: 'Jun 02, 2026' },
        { action: 'Connect IPC – Paint Booth 2',      priority: 'Medium', dueDate: 'Jun 03, 2026' },
        { action: 'Verify data collection – Sub Assy', priority: 'Low',   dueDate: 'Jul 07, 2026' },
        { action: 'Update maintenance zone mapping',  priority: 'Low',    dueDate: 'Jun 09, 2026' }
      ],
      timeline: [
        { area: 'Main Line',        color: '#3d7cbf', plannedStart: 0, plannedEnd: 9,  inProgressStart: 5, inProgressEnd: 9,  deployedStart: 0, deployedEnd: 5,  progressPct: 60, progressDelta: '▲ 8%' },
        { area: 'Sub Assembly',     color: '#f58220', plannedStart: 0, plannedEnd: 9,  inProgressStart: 3, inProgressEnd: 9,  deployedStart: 0, deployedEnd: 6,  progressPct: 72, progressDelta: '▲ 10%' },
        { area: 'Final Assembly',   color: '#7d4db5', plannedStart: 0, plannedEnd: 11, inProgressStart: 6, inProgressEnd: 11, deployedStart: 0, deployedEnd: 7,  progressPct: 90, progressDelta: '▲ 12%' },
        { area: 'Paint Booths',     color: '#31a36f', plannedStart: 0, plannedEnd: 10, inProgressStart: 4, inProgressEnd: 10, deployedStart: 0, deployedEnd: 7,  progressPct: 85, progressDelta: '▲ 5%' },
        { area: 'Test & Rework',    color: '#3eb8c9', plannedStart: 0, plannedEnd: 4,  deployedStart: 0, deployedEnd: 4,                                          progressPct: 100, progressDelta: '—' },
        { area: 'Material Staging', color: '#d94b4b', plannedStart: 1, plannedEnd: 6,  inProgressStart: 4, inProgressEnd: 6,  deployedStart: 1, deployedEnd: 2,  progressPct: 40, progressDelta: '▲ 3%' }
      ],
      metadata: {
        shopAdmin: 'J. Anderson', targetStart: 'Jul 15, 2025',
        targetPilotLineComplete: 'Jan 31, 2026', deploymentPhase: 'Post Pilot-Line',
        lastUpdatedBy: 'J. Anderson', lastUpdated: 'May 24, 2026  10:15 AM'
      }
    },
    'TMMMO|Body Weld Plant|Paint Shop': {
      zones: [
        { name: 'Prep Zone',       deploymentPct: 55, color: '#3d7cbf' },
        { name: 'Paint Zone A',    deploymentPct: 80, color: '#31a36f' },
        { name: 'Oven Zone',       deploymentPct: 45, color: '#f58220' },
        { name: 'Inspection Zone', deploymentPct: 20, color: '#d94b4b' }
      ],
      equipment: [
        { category: 'PLCs',           planned: 32,  configured: 22, connected: 18, statusPct: 56, statusLevel: 'warn' },
        { category: 'Sensors',        planned: 120, configured: 75, connected: 55, statusPct: 46, statusLevel: 'critical' },
        { category: 'Gateways',       planned: 4,   configured: 3,  connected: 2,  statusPct: 50, statusLevel: 'warn' },
        { category: 'Industrial PCs', planned: 8,   configured: 5,  connected: 4,  statusPct: 50, statusLevel: 'warn' }
      ],
      openActions: [
        { action: 'Commission oven zone PLCs',         priority: 'High',   dueDate: 'Jun 05, 2026' },
        { action: 'Install inspection zone sensors',   priority: 'High',   dueDate: 'Jun 12, 2026' },
        { action: 'Validate paint zone data stream',   priority: 'Medium', dueDate: 'Jun 18, 2026' }
      ],
      timeline: [
        { area: 'Prep Zone',       color: '#3d7cbf', plannedStart: 2, plannedEnd: 9,  inProgressStart: 6, inProgressEnd: 9,  deployedStart: 2, deployedEnd: 5, progressPct: 55, progressDelta: '▲ 6%' },
        { area: 'Paint Zone A',    color: '#31a36f', plannedStart: 1, plannedEnd: 8,  inProgressStart: 5, inProgressEnd: 8,  deployedStart: 1, deployedEnd: 6, progressPct: 80, progressDelta: '▲ 9%' },
        { area: 'Oven Zone',       color: '#f58220', plannedStart: 3, plannedEnd: 10, inProgressStart: 7, inProgressEnd: 10,                                    progressPct: 45, progressDelta: '▲ 4%' },
        { area: 'Inspection Zone', color: '#d94b4b', plannedStart: 5, plannedEnd: 11, inProgressStart: 9, inProgressEnd: 11,                                    progressPct: 20, progressDelta: '▲ 2%' }
      ],
      metadata: {
        shopAdmin: 'R. Torres', targetStart: 'Sep 01, 2025',
        targetPilotLineComplete: 'Mar 15, 2026', deploymentPhase: 'Pilot-Line',
        lastUpdatedBy: 'R. Torres', lastUpdated: 'May 20, 2026  2:30 PM'
      }
    },
    'TMMMO|Assembly Plant|Assembly Shop': {
      notStarted: true,
      zones: [], equipment: [], openActions: [], timeline: [],
      metadata: { shopAdmin: 'TBD', targetStart: 'TBD', targetPilotLineComplete: 'TBD', deploymentPhase: 'Not Started', lastUpdatedBy: '—', lastUpdated: '—' }
    },
    'TMMMO|Plastics Plant|Plastics Shop': {
      notStarted: true,
      zones: [], equipment: [], openActions: [], timeline: [],
      metadata: { shopAdmin: 'TBD', targetStart: 'TBD', targetPilotLineComplete: 'TBD', deploymentPhase: 'Not Started', lastUpdatedBy: '—', lastUpdated: '—' }
    },
    'TMMI|East Body Weld Plant|East Body Weld Shop': {
      zones: [
        { name: 'Frame Zone',     deploymentPct: 98, color: '#31a36f' },
        { name: 'Side Body Zone', deploymentPct: 95, color: '#3d7cbf' },
        { name: 'Underbody',      deploymentPct: 92, color: '#7d4db5' },
        { name: 'Roof Zone',      deploymentPct: 88, color: '#f58220' }
      ],
      equipment: [
        { category: 'PLCs',        planned: 95,  configured: 95,  connected: 93,  statusPct: 98,  statusLevel: 'good' },
        { category: 'DXM Modules', planned: 72,  configured: 70,  connected: 68,  statusPct: 94,  statusLevel: 'good' },
        { category: 'Sensors',     planned: 310, configured: 308, connected: 295, statusPct: 95,  statusLevel: 'good' },
        { category: 'Gateways',    planned: 8,   configured: 8,   connected: 8,   statusPct: 100, statusLevel: 'good' }
      ],
      openActions: [
        { action: 'Replace faulty sensor – Roof Zone P3', priority: 'Medium', dueDate: 'Jun 01, 2026' },
        { action: 'PLC firmware update – Frame Zone',     priority: 'Low',    dueDate: 'Jun 15, 2026' }
      ],
      timeline: [
        { area: 'Frame Zone',     color: '#31a36f', plannedStart: 0, plannedEnd: 6, deployedStart: 0, deployedEnd: 6, progressPct: 98, progressDelta: '—' },
        { area: 'Side Body Zone', color: '#3d7cbf', plannedStart: 0, plannedEnd: 7, deployedStart: 0, deployedEnd: 7, progressPct: 95, progressDelta: '—' },
        { area: 'Underbody',      color: '#7d4db5', plannedStart: 1, plannedEnd: 7, deployedStart: 1, deployedEnd: 7, progressPct: 92, progressDelta: '▲ 1%' },
        { area: 'Roof Zone',      color: '#f58220', plannedStart: 2, plannedEnd: 9, inProgressStart: 8, inProgressEnd: 9, deployedStart: 2, deployedEnd: 8, progressPct: 88, progressDelta: '▲ 2%' }
      ],
      metadata: {
        shopAdmin: 'K. Yamamoto', targetStart: 'Jul 01, 2025',
        targetPilotLineComplete: 'Dec 31, 2025', deploymentPhase: 'NAMC Scaling',
        lastUpdatedBy: 'K. Yamamoto', lastUpdated: 'May 22, 2026  9:00 AM'
      }
    },
    'TMMI|West Assembly Plant|West Assembly Shop': {
      zones: [
        { name: 'Trim Line',    deploymentPct: 96, color: '#31a36f' },
        { name: 'Chassis Line', deploymentPct: 94, color: '#3d7cbf' },
        { name: 'Final Line',   deploymentPct: 91, color: '#7d4db5' }
      ],
      equipment: [
        { category: 'PLCs',        planned: 88,  configured: 88,  connected: 85,  statusPct: 97, statusLevel: 'good' },
        { category: 'DXM Modules', planned: 60,  configured: 60,  connected: 57,  statusPct: 95, statusLevel: 'good' },
        { category: 'Sensors',     planned: 280, configured: 278, connected: 265, statusPct: 95, statusLevel: 'good' }
      ],
      openActions: [
        { action: 'DXM module swap – Final Line bay 7', priority: 'Low', dueDate: 'Jun 10, 2026' }
      ],
      timeline: [
        { area: 'Trim Line',    color: '#31a36f', plannedStart: 0, plannedEnd: 5, deployedStart: 0, deployedEnd: 5, progressPct: 96, progressDelta: '—' },
        { area: 'Chassis Line', color: '#3d7cbf', plannedStart: 0, plannedEnd: 6, deployedStart: 0, deployedEnd: 6, progressPct: 94, progressDelta: '—' },
        { area: 'Final Line',   color: '#7d4db5', plannedStart: 1, plannedEnd: 8, inProgressStart: 7, inProgressEnd: 8, deployedStart: 1, deployedEnd: 7, progressPct: 91, progressDelta: '▲ 1%' }
      ],
      metadata: {
        shopAdmin: 'M. Patel', targetStart: 'Jul 01, 2025',
        targetPilotLineComplete: 'Jan 15, 2026', deploymentPhase: 'NAMC Scaling',
        lastUpdatedBy: 'M. Patel', lastUpdated: 'May 23, 2026  11:45 AM'
      }
    },
    'TMMK|Assembly Plant|Assembly Shop': {
      zones: [
        { name: 'Trim Zone',    deploymentPct: 88, color: '#31a36f' },
        { name: 'Chassis Zone', deploymentPct: 75, color: '#f58220' },
        { name: 'Finish Zone',  deploymentPct: 62, color: '#3d7cbf' }
      ],
      equipment: [
        { category: 'PLCs',     planned: 55,  configured: 48,  connected: 45,  statusPct: 82, statusLevel: 'good' },
        { category: 'Sensors',  planned: 170, configured: 140, connected: 128, statusPct: 75, statusLevel: 'warn' },
        { category: 'Gateways', planned: 5,   configured: 4,   connected: 4,   statusPct: 80, statusLevel: 'good' }
      ],
      openActions: [
        { action: 'Sensor install – Chassis Zone stations 4-6', priority: 'High',   dueDate: 'May 30, 2026' },
        { action: 'Validate Finish Zone PLC config',            priority: 'Medium', dueDate: 'Jun 08, 2026' }
      ],
      timeline: [
        { area: 'Trim Zone',    color: '#31a36f', plannedStart: 0, plannedEnd: 8,  inProgressStart: 6, inProgressEnd: 8, deployedStart: 0, deployedEnd: 6, progressPct: 88, progressDelta: '▲ 5%' },
        { area: 'Chassis Zone', color: '#f58220', plannedStart: 1, plannedEnd: 9,  inProgressStart: 5, inProgressEnd: 9, deployedStart: 1, deployedEnd: 5, progressPct: 75, progressDelta: '▲ 7%' },
        { area: 'Finish Zone',  color: '#3d7cbf', plannedStart: 3, plannedEnd: 10, inProgressStart: 7, inProgressEnd: 10,                                   progressPct: 62, progressDelta: '▲ 4%' }
      ],
      metadata: {
        shopAdmin: 'T. Williams', targetStart: 'Aug 01, 2025',
        targetPilotLineComplete: 'Feb 28, 2026', deploymentPhase: 'Model Line Deployment',
        lastUpdatedBy: 'T. Williams', lastUpdated: 'May 19, 2026  3:00 PM'
      }
    },
    'TMMK|Weld Plant|Weld Shop': {
      notStarted: true,
      zones: [], equipment: [], openActions: [], timeline: [],
      metadata: { shopAdmin: 'TBD', targetStart: 'TBD', targetPilotLineComplete: 'TBD', deploymentPhase: 'Not Started', lastUpdatedBy: '—', lastUpdated: '—' }
    }
  };

  // ── NAMC-Shop Detail state ────────────────────────────────────────────────
  selectedShopNamc = '';
  selectedShopPlant = '';
  selectedShopArea = '';

  selectedCompany: number | null = null;
  selectedPlant: number | null = null;
  selectedShop: number | null = null;
  currentCompany: any = null;
  currentPlant: any = null;
  companyList: any[];
  loader = { isLoading: true, message: 'Loading...' };
  errorMessage = '';
  selectedPin: DeploymentMapPin | null = {
    labelDirection: 'up',
    left: '52.5%',
    top: '75.5%',
    code: 'ASSEMBLY',
    value: '83',
    companyId: 7857,
    codePlant: 'TMMC',
    status: 'enabled'
  };

   trackByMapCode(_: number, pin: DeploymentMapPin): string    { return pin.code; }

   readonly deploymentMapPins: DeploymentMapPin[] = [
      {
        labelDirection:"up",
        left:"70.5%",
        top:"49.5%",
        code: "PAINT",
        value: "N/A",
        companyId: 7857,
        codePlant: "TMMC",
        status: "enabled"
      },
      {
        labelDirection:"up",
        left:"45.5%",
        top:"35.5%",
        code: "WELD",
        value: "N/A",
        companyId: 7857,
        codePlant: "TMMC",
        status: "enabled"
      },
       {
        labelDirection:"up",
        left:"15.5%",
        top:"30.5%",
        code: "STAMPING",
        value: "35",
        companyId: 7857,
        codePlant: "TMMC",
        status: "enabled"
      },
      {
        labelDirection:"up",
        left:"52.5%",
        top:"75.5%",
        code: "ASSEMBLY",
        value: "57",
        companyId: 7857,
        codePlant: "TMMC",
        status: "enabled"
      }
   ];

  // Dynamic right-panel data per shop pin (complete for all map shops)
  readonly pinDashboardByCode: Record<string, PinDashboardData> = {
    STAMPING: {
      phase: 'Pilot-Line',
      nextMilestone: 'Sensor mapping closure - Sep 02, 2026',
      shopConnectivityPct: 35,
      equipmentConnected: 75,
      equipmentPlanned: 170,
      pdm: { planned: 1510, connected: 862, statusPct: 30 }
    },
    WELD: {
      phase: 'Pre-Deployment',
      nextMilestone: 'Infrastructure readiness review - Oct 10, 2026',
      shopConnectivityPct: null,
      equipmentConnected: 0,
      equipmentPlanned: 170,
      pdm: { planned: 1510, connected: 862, statusPct: 30 }
    },
    PAINT: {
      phase: 'Pre-Deployment',
      nextMilestone: 'Network commissioning kickoff - Oct 24, 2026',
      shopConnectivityPct: null,
      equipmentConnected: 0,
      equipmentPlanned: 170,
      pdm: { planned: 1510, connected: 862, statusPct: 30 }
    },
    ASSEMBLY: {
      phase: 'NAMC Scaling',
      nextMilestone: 'Final line validation - Aug 29, 2026',
      shopConnectivityPct: 57,
      equipmentConnected: 142,
      equipmentPlanned: 170,
      pdm: { planned: 1510, connected: 460, statusPct: 30 }
    }
  };

  constructor(public readonly eventService: MfgappsEventService) { }
  
   ngOnInit() {
    this.initializeComponent();
  }

  async initializeComponent() {
    try {
      this.loader.message = 'Loading Corporate Hierarchy...';
      this.companyList = await this.eventService.getCorporateHierarchy();
      this.resetLoader();
    }
    catch (error) {
      console.info(error);
      this.resetLoader();
      this.errorMessage = "Error occurred while Loading Corporate Hierarchy";
      this.eventService.showSnackBar(this.errorMessage, 'error');
    }
  }

  resetLoader() {
    this.loader.isLoading = false;
    this.loader.message = '';
  }

  isMapNamcSelected(code: string): boolean {
    return this.selectedPin?.code === code;
  }

  getMapPinAriaLabel(pin: DeploymentMapPin): string {
    return `${pin.code}: ${pin.codePlant} (${pin.value}${pin.value !== 'N/A' ? '%' : ''})`;
  }

  selectMapNamc(pin: DeploymentMapPin): void {
    if (this.selectedPin?.code === pin.code) {
      return;
    }
    this.selectedPin = pin;
    this.selectedShopArea = pin.code;
    this.syncShopFilterWithPin(pin.code);
  }

  get selectedPinDashboard(): PinDashboardData | null {
    if (!this.selectedPin) { return null; }
    return this.pinDashboardByCode[this.selectedPin.code] ?? null;
  }

  get selectedPinConnectivityLabel(): string {
    const pct = this.selectedPinDashboard?.shopConnectivityPct;
    if (pct === null || pct === undefined) { return 'N/A'; }
    return `${pct}%`;
  }

  get connectivityIconClass(): string {
    const pct = this.selectedPinDashboard?.shopConnectivityPct;
    if (pct === null || pct === undefined) { return 'conn-kpi-icon--teal'; }
    return pct <= 35 ? 'conn-kpi-icon--red' : 'conn-kpi-icon--teal';
  }

  get selectedPinStatusPct(): number {
    return this.selectedPinDashboard?.pdm.statusPct ?? 0;
  }

  private syncShopFilterWithPin(pinCode: string): void {
    const shopList = this.currentPlant?.shopList ?? [];
    if (!Array.isArray(shopList) || !shopList.length) { return; }

    const pinKey = pinCode.toUpperCase();
    const matchedShop = shopList.find((shop: any) =>
      String(shop?.shop_name ?? '').toUpperCase().includes(pinKey)
    );

    if (matchedShop?.shop_id) {
      this.selectedShop = matchedShop.shop_id;
    }
  }

  get shopDetailFiltersComplete(): boolean {
    return !!this.selectedCompany && !!this.selectedPlant;
  }

  get currentNamcShopDetail(): NamcShopDetailData | null {
    if (!this.shopDetailFiltersComplete) { return null; }
    const key = `${this.selectedShopNamc}|${this.selectedShopPlant}|${this.selectedShopArea}`;
    return this.namcShopMockData[key] ?? null;
  }


  async onCompanyChange() {
    this.currentCompany = this.companyList.find(company => company.company_id === this.selectedCompany);
    this.selectedPlant = null;
  }

  async onPlantChange() {
    this.currentPlant = this.currentCompany?.plantList.find(plant => plant.plant_id === this.selectedPlant);
  }

}
